import { useToast } from '@chakra-ui/react';
import { useState } from 'react';

import {
	requestCollaborationExport,
	requestConsensusExport,
	requestRestrictedPlanExport,
} from '../services/api/requests/asyncExportRequests';
import {
	AsyncJobProcessTypeEnum,
	RequestExportResponse,
} from '../types/asyncExport.types';

const ASYNC_JOB_TYPE_LABELS: Record<AsyncJobProcessTypeEnum, string> = {
	[AsyncJobProcessTypeEnum.EXPORT_CONSENSUS]: 'Consenso',
	[AsyncJobProcessTypeEnum.EXPORT_RESTRICTED_PLAN]: 'Plano Restrito',
	[AsyncJobProcessTypeEnum.EXPORT_PLANNING_COLLAB]:
		'Colaboração de Planejamento',
	[AsyncJobProcessTypeEnum.EXPORT_MARKETING_COLLAB]:
		'Colaboração de Marketing',
	[AsyncJobProcessTypeEnum.EXPORT_COMMERCIAL_COLLAB]: 'Colaboração Comercial',
	[AsyncJobProcessTypeEnum.GENERATE_RESTRICTED_PLAN]: 'Plano Restrito',
};

const getFriendlyMessage = (message: string): string => {
	if (!message) {
		return 'Você atingiu o limite de exportações. Por favor, aguarde alguns minutos antes de tentar novamente.';
	}

	let friendlyMessage = message;
	Object.entries(ASYNC_JOB_TYPE_LABELS).forEach(
		([techType, friendlyType]) => {
			friendlyMessage = friendlyMessage.replaceAll(
				`"${techType}"`,
				`"${friendlyType}"`
			);
			friendlyMessage = friendlyMessage.replaceAll(
				techType,
				friendlyType
			);
		}
	);

	return friendlyMessage;
};

interface UseAsyncExportParams {
	processType: AsyncJobProcessTypeEnum;
	cycleId?: number;
	companyAreaId?: number;
	collaborationType?: 'planning' | 'marketing' | 'commercial';
	onExportStarted?: () => void;
}

interface UseAsyncExportReturn {
	handleExport: () => Promise<void>;
	isLoading: boolean;
}

export const useAsyncExport = (
	params: UseAsyncExportParams
): UseAsyncExportReturn => {
	const [isLoading, setIsLoading] = useState(false);
	const toast = useToast();

	const requestExport = async (): Promise<RequestExportResponse> => {
		switch (params.processType) {
			case AsyncJobProcessTypeEnum.EXPORT_RESTRICTED_PLAN:
				return await requestRestrictedPlanExport({
					cycle_id: params.cycleId,
					company_area_id: params.companyAreaId,
				});

			case AsyncJobProcessTypeEnum.EXPORT_CONSENSUS:
				return await requestConsensusExport(params.cycleId);

			case AsyncJobProcessTypeEnum.EXPORT_PLANNING_COLLAB:
			case AsyncJobProcessTypeEnum.EXPORT_MARKETING_COLLAB:
			case AsyncJobProcessTypeEnum.EXPORT_COMMERCIAL_COLLAB:
				if (!params.collaborationType) {
					throw new Error(
						'collaborationType is required for collaboration exports'
					);
				}
				return await requestCollaborationExport({
					collaboration_type: params.collaborationType,
					cycle_id: params.cycleId,
					company_area_id: params.companyAreaId,
				});

			default:
				throw new Error(
					`Unsupported process type: ${params.processType}`
				);
		}
	};

	const handleExport = async (): Promise<void> => {
		setIsLoading(true);
		try {
			await requestExport();

			toast({
				title: 'Sua exportação foi iniciada. Assim que o arquivo estiver pronto, o download estará disponível em Atividades Recentes no sistema e no seu e-mail',
				status: 'success',
				duration: 8000,
				isClosable: true,
				position: 'bottom-left',
			});

			if (params.onExportStarted) {
				params.onExportStarted();
			}

			setIsLoading(false);
		} catch (error: unknown) {
			setIsLoading(false);

			const errorResponse = error as {
				response?: { status?: number; data?: { message?: string } };
			};
			const status = errorResponse?.response?.status;

			if (status === 409) {
				toast({
					title: 'Já existe uma exportação em andamento para este tipo.',
					status: 'warning',
					duration: 8000,
					isClosable: true,
					position: 'bottom-left',
				});
			} else if (status === 429) {
				const backendMessage =
					errorResponse?.response?.data?.[0]?.message ||
					errorResponse?.response?.data?.message ||
					'';

				toast({
					title: 'Limite de exportações atingido.',
					description: getFriendlyMessage(backendMessage),
					status: 'error',
					duration: 8000,
					isClosable: true,
					position: 'bottom-left',
				});
			} else {
				toast({
					title: 'Não foi possível iniciar sua exportação agora.',
					description:
						'Só é permitido 10 exportações por hora. Tente novamente mais tarde.',
					status: 'error',
					duration: 8000,
					isClosable: true,
					position: 'bottom-left',
				});
			}

			throw error;
		}
	};

	return {
		handleExport,
		isLoading,
	};
};
